import type Phaser from 'phaser';
import type { CameraConfig } from '../config/gameConfig';
import type { IsoProjection } from '../core/iso';
import type { ScreenPoint, WorldPoint } from '../core/types';
import { clampToMap, clampZoom, panByScreenDelta, screenToWorld, scrollForCenter, scrollForScreenAnchor, viewCenter, type ViewState } from './cameraMath';
import { ZoomAnimator } from './ZoomAnimator';

/**
 * Applies pan / zoom to the Phaser camera using the pure maths in
 * cameraMath.ts and ZoomAnimator.ts.
 *
 *  - Every change goes through `apply`, which clamps the view centre to the
 *    map diamond and notifies listeners exactly once.
 *  - Smooth zoom is advanced from the scene's single update loop (`update`);
 *    this class never starts its own timers or animation loops.
 *  - On viewport resize the world point at the centre stays at the centre.
 */
export class CameraController {
  private readonly listeners = new Set<() => void>();
  private readonly zoomer: ZoomAnimator;
  private lastCenter: WorldPoint = { x: 0, y: 0 };

  constructor(
    private readonly cam: Phaser.Cameras.Scene2D.Camera,
    private readonly cfg: CameraConfig,
    private readonly iso: IsoProjection,
    private readonly mapW: number,
    private readonly mapH: number,
  ) {
    this.zoomer = new ZoomAnimator(cfg.zoomSmoothing, cfg.minZoom, cfg.maxZoom);
    cam.setZoom(clampZoom(cfg.defaultZoom, cfg.minZoom, cfg.maxZoom));
    cam.setRoundPixels(false);
  }

  get zoom(): number {
    return this.cam.zoom;
  }

  /** Where an in-progress smooth zoom will land (current zoom when idle). */
  get targetZoom(): number {
    return this.zoomer.targetZoom(this.cam.zoom);
  }

  get isZooming(): boolean {
    return this.zoomer.active;
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  onChange(l: () => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  view(): ViewState {
    return {
      scrollX: this.cam.scrollX,
      scrollY: this.cam.scrollY,
      zoom: this.cam.zoom,
      viewWidth: this.cam.width,
      viewHeight: this.cam.height,
    };
  }

  /** Advance smooth zoom. Called once per frame by the scene. */
  update(dt: number): void {
    const next = this.zoomer.step(this.view(), dt);
    if (next) this.apply(next);
  }

  centerOnGrid(gx: number, gy: number): void {
    const w = this.iso.gridToWorld(gx, gy);
    this.apply({ ...this.view(), ...scrollForCenter(this.view(), w.x, w.y) });
  }

  pan(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;
    this.apply(panByScreenDelta(this.view(), dx, dy));
  }

  /** Smoothly multiply zoom, anchored at a screen point (null = viewport centre). */
  zoomBy(factor: number, screen: ScreenPoint | null): void {
    const v = this.view();
    const anchor = screen ?? { x: v.viewWidth / 2, y: v.viewHeight / 2 };
    if (this.zoomer.request(v, factor, anchor) && this.cfg.zoomSmoothing <= 0) this.update(0);
  }

  /**
   * Two-finger gesture step, applied immediately (fingers expect no lag):
   * the world point under `from` ends up under `to` at the new zoom, so the map
   * stays glued to the fingers while zooming and panning together.
   */
  pinch(factor: number, from: ScreenPoint, to: ScreenPoint): void {
    this.zoomer.cancel();
    const v = this.view();
    const zoom = clampZoom(v.zoom * factor, this.cfg.minZoom, this.cfg.maxZoom);
    const world = screenToWorld(v, from.x, from.y);
    this.apply({ ...v, zoom, ...scrollForScreenAnchor(v, zoom, world, to.x, to.y) });
  }

  /** Jump to a zoom immediately about the viewport centre (cancels any smooth zoom). */
  setZoomImmediate(zoom: number): void {
    this.zoomer.cancel();
    const v = this.view();
    const z = clampZoom(zoom, this.cfg.minZoom, this.cfg.maxZoom);
    const c = viewCenter(v);
    this.apply({ ...v, zoom: z, ...scrollForCenter(v, c.x, c.y) });
  }

  /** Viewport was resized: keep the same world point in the centre. */
  handleResize(): void {
    const v = this.view();
    this.apply({ ...v, ...scrollForCenter(v, this.lastCenter.x, this.lastCenter.y) });
  }

  private apply(v: ViewState): void {
    const c = clampToMap(v, this.iso, this.mapW, this.mapH, this.cfg.boundsPaddingTiles);
    this.cam.setZoom(c.zoom);
    this.cam.setScroll(c.scrollX, c.scrollY);
    const applied = this.view();
    this.lastCenter = viewCenter(applied);
    // Anything other than the animator (pan, clamp, resize) may have moved the
    // camera: re-pin the zoom anchor so the two never fight each other.
    this.zoomer.reanchor(applied);
    for (const l of this.listeners) l();
  }
}
